import {Box, Button, CircularProgress, Input, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {CloudUploadIcon} from "../icons/CloudUploadIcon";
import {useRef, useState} from "react";
import {bulkCreateAppliedIrrigation} from "../../services/appliedIrrigationService";
import {CheckCircleOutline, RemoveCircleOutline} from "@mui/icons-material";

interface IProps {
    companyId: string;
}

const MAX_CSV_DATA_ROWS = 30_000;

export const RainfallIrrigationCsvUpload: React.FC<IProps> = (props) => {
    const {companyId} = props;
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [uploaded, setUploaded] = useState(false);
    const [error, setError] = useState<"unknown" | "date_format" | "size_limit">();

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const uploadCSV = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const file = event.target.files?.[0];
            setUploaded(false);
            if (file) {
                const reader = new FileReader();

                reader.onload = async () => {
                    setLoading(true);
                    const text = reader.result as string;

                    const rows = text
                        .split("\n")
                        .map((row) => row.trim())
                        .filter((row) => row.length > 0)
                        .map((row) => row.split(",").map((cell) => cell.replace(/\r/g, "").trim()));

                    // Return if the data row count exceeds the limit (excluding the header)
                    if (rows.length - 1 > MAX_CSV_DATA_ROWS) {
                        setError("size_limit");
                        setLoading(false);
                        event.target.value = "";
                        return;
                    }

                    const headers = rows[0].map((header) => header.replace(/\r/g, "").trim());

                    const jsonArray = rows.slice(1).map((row) => {
                        const obj: Record<string, string> = {};
                        row.forEach((cell, index) => {
                            const key = headers[index];
                            obj[key] = cell;
                        });
                        return obj;
                    });

                    // Check for valid date format
                    if (jsonArray.some((row) => !/^\d{4}-\d{2}-\d{2}$/.test(row.date))) {
                        setError("date_format");
                        setLoading(false);
                        event.target.value = "";
                        return;
                    }

                    const fields = jsonArray.map((row) => ({
                        id: row.fieldId,
                        irrigation: parseFloat(row.irrigation),
                        rainfall: parseFloat(row.rainfall),
                        date: row.date,
                    }));

                    try {
                        await bulkCreateAppliedIrrigation({
                            date: undefined,
                            companyId,
                            reprocessNotif: true,
                            fields,
                        });
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (e) {
                        setError("unknown");
                        setLoading(false);
                        event.target.value = "";
                        return;
                    }

                    setTimeout(() => {
                        event.target.value = "";
                        setError(undefined);
                        setLoading(false);
                        setUploaded(true);
                    }, 2000);
                };

                reader.readAsText(file);
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            setError("unknown");
            setLoading(false);
            event.target.value = "";
        }
    };

    const getErrorText = () => {
        if (!error) return "";

        const texts = [t("data_tools.irrigation_rainfall_data_error")];

        if (error === "size_limit") {
            texts.push(
                t("data_tools.irrigation_rainfall_data_error_size_limit", {
                    limit: MAX_CSV_DATA_ROWS,
                }),
            );
        }

        if (error === "date_format") texts.push(t("data_tools.irrigation_rainfall_data_error_date_format"));

        return (
            <>
                {texts.map((text) => (
                    <>
                        <span>{text}</span>
                        <br />
                    </>
                ))}
            </>
        );
    };

    return (
        <Box
            style={{
                padding: "15px 25px",
            }}
        >
            <Typography variant="h2" component="h3" marginBottom="20px">
                {t("dialogs.upload_irrigation_rainfall.title")}
            </Typography>
            {uploaded && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    p={1}
                    borderRadius="5px"
                    borderColor="success.main"
                    bgcolor="transparent"
                >
                    <CheckCircleOutline color="success" />
                    <Typography color="success.main" variant="body1">
                        {t("data_tools.irrigation_rainfall_data_added")}
                    </Typography>
                </Box>
            )}
            {error && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    p={1}
                    borderRadius="5px"
                    borderColor="error.main"
                    bgcolor="transparent"
                >
                    <RemoveCircleOutline color="error" />
                    <Typography color="error.main" variant="body1">
                        {getErrorText()}
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "20px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "15px",
                    }}
                >
                    <Button
                        onClick={uploadCSV}
                        variant={"outlined"}
                        sx={{
                            minWidth: "100px",
                        }}
                    >
                        {loading ? (
                            <CircularProgress size="1.25rem" />
                        ) : (
                            <CloudUploadIcon
                                sx={{
                                    stroke: "#404DB8",
                                    width: "1.25rem",
                                    height: "1.25rem",
                                }}
                            />
                        )}
                        &nbsp; {t("data_tools.upload_csv")}
                    </Button>

                    <Input type="file" inputRef={fileInputRef} onChange={handleFileChange} style={{display: "none"}} />
                </Box>
            </Box>
        </Box>
    );
};
